import { useState, useEffect, useRef } from "react";
import { GOLD, NAVY, WHITE } from "../styles/palette";
import { getAuthToken, sb, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { loadStripe } from "../lib/stripe";
import { ScoopMark } from "./ScoopMark";
import { ServiceAreaEditor } from "./ServiceAreaEditor";
import { PricingEditor } from "./PricingEditor";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { formatPhoneInput, onlyDigits, isValidPhone, isValidZip, US_STATES, detectedTz, tzOptionsFor } from "../lib/format";
import { useIsMobile } from "../lib/useIsMobile";

// Phase C — Operator onboarding wizard.
// Shown to an owner whose org.onboarding_completed is false. Walks them
// through business profile → service area → pricing → first technician,
// then flips onboarding_completed=true so the normal CRM takes over.
const STEPS = ["Business", "Plan & Payment", "Service Area", "Pricing", "Technicians"];

export function OnboardingWizard({ org, serviceArea, technicians, clients, showToast, onRefresh, onComplete }) {
  const isMobile = useIsMobile();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState(""); // inline validation (toast isn't visible in the full-screen wizard)

  // App renders this wizard INSTEAD of the main shell, so App's toast element is never in the
  // tree while we're shown — anything sent to showToast here is silently lost. Every message
  // (ours + the embedded Service-area / Pricing editors') goes through this in-wizard notice,
  // pinned to the top of the phone screen so it's seen wherever the owner has scrolled.
  const [notice, setNotice] = useState(null); // { msg, type }
  const noticeTimer = useRef(null);
  const notify = (msg, type = "success") => {
    setNotice({ msg, type });
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), type === "error" ? 7000 : 3500);
  };
  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  // Step 1 — business profile fields (seeded from whatever's on the org)
  const [biz, setBiz] = useState({
    name:         org?.name || "",
    contact_name: org?.contact_name || "",
    phone:        org?.phone || "",
    address:      org?.address || "",
    city:         org?.city || "",
    state:        org?.state || "",
    zip:          org?.zip || "",
    timezone:     org?.timezone || detectedTz(),
    website:      org?.website || "",
  });

  // Step 2 — plan choice + card on the PLATFORM account (Phase D.3)
  const [plan, setPlan] = useState(org?.saas_plan || "percentage");
  const [cardErr, setCardErr] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const stripeRef = useRef(null);
  const cardElRef = useRef(null);
  const cardMounted = useRef(false);

  // Step 4 — add a technician inline
  const [tName, setTName] = useState("");
  const [tEmail, setTEmail] = useState("");

  // Mount a PLATFORM card element when the Plan step opens (operator pays the platform).
  useEffect(() => {
    if (step !== 1 || cardMounted.current) return;
    cardMounted.current = true;
    loadStripe().then(stripe => {           // no account => platform
      stripeRef.current = stripe;
      const card = stripe.elements().create("card", {
        // 16px on phones: an iframe input under 16px makes iOS Safari zoom the page on focus and never zoom back
        style: { base: { fontFamily: "Inter, sans-serif", fontSize: isMobile ? "16px" : "14px", color: "#1a1a2e", "::placeholder": { color: "#a0aec0" } } },
        hidePostalCode: false,
      });
      card.mount("#operator-card-element");
      card.on("change", e => { setCardErr(e.error?.message || ""); setCardComplete(e.complete); });
      cardElRef.current = card;
    }).catch(() => setErr("Could not load the secure card form. Refresh and try again."));
    return () => { if (cardElRef.current) { cardElRef.current.destroy(); cardMounted.current = false; } };
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  async function savePlan() {
    setErr(""); setCardErr("");
    if (!cardComplete) { setCardErr("Please enter your card details to continue."); return; }
    setBusy(true);
    try {
      // 1) create a SetupIntent on the platform account
      const init = await sb.fn("operator-billing", { action: "init" });
      if (init.error || !init.client_secret) throw new Error(init.error || "Could not start card setup");
      // 2) confirm (save) the card against it — no charge
      const { setupIntent, error } = await stripeRef.current.confirmCardSetup(init.client_secret, {
        payment_method: { card: cardElRef.current, billing_details: { name: biz.contact_name || org?.name || undefined } },
      });
      if (error) { setCardErr(error.message); setBusy(false); return; }
      // 3) store plan + card; create the $99 sub if they picked flat
      const fin = await sb.fn("operator-billing", { action: "finalize", plan, payment_method_id: setupIntent.payment_method });
      if (fin.error) throw new Error(fin.error);
      await onRefresh();
      setBusy(false);
      setStep(2);
    } catch (e) {
      setBusy(false);
      setErr(e.message || "Something went wrong saving your plan. Please try again.");
    }
  }

  const patchOrg = async (body) => {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/organizations?id=eq.${org.id}`, {
      method: "PATCH",
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json", Prefer: "return=minimal" },
      body: JSON.stringify(body),
    });
    return r.ok;
  };

  async function saveBusiness() {
    setErr("");
    if (!biz.name.trim())         { setErr("Please enter your business name to continue."); return; }
    if (!biz.contact_name.trim()) { setErr("Please enter your name to continue."); return; }
    if (!biz.address.trim())      { setErr("Please add your business address — start typing and pick it from the list."); return; }
    if (!isValidPhone(biz.phone)) { setErr("Phone number should be 10 digits (or leave it blank)."); return; }
    if (!isValidZip(biz.zip))     { setErr("Business ZIP should be 5 digits (or leave it blank)."); return; }
    setBusy(true);
    const ok = await patchOrg({
      name:    biz.name.trim(),
      contact_name: biz.contact_name.trim(),
      phone:   biz.phone.trim()   || null,
      address: biz.address.trim() || null,
      city:    biz.city.trim()    || null,
      state:   biz.state.trim()   || null,
      zip:     biz.zip.trim()     || null,
      timezone: biz.timezone || null,
      website: biz.website.trim() ? (/^https?:\/\//i.test(biz.website.trim()) ? biz.website.trim() : "https://" + biz.website.trim()) : null,
    });
    setBusy(false);
    if (!ok) { setErr("Could not save business info — check your connection and try again."); return; }
    await onRefresh();
    setStep(1);
  }

  async function addTech() {
    if (!tName.trim() || !tEmail.trim()) return;
    const emailLc = tEmail.trim().toLowerCase();
    if ((technicians||[]).some(t => (t.email||"").toLowerCase() === emailLc)) {
      notify("A technician with this email already exists.", "error"); return;
    }
    setBusy(true);
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/technicians`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({ name: tName.trim(), email: tEmail.trim(), active: true, has_account: true }),
      });
      const techs = await fetch(`${SUPABASE_URL}/rest/v1/technicians?order=name.asc`, { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` } }).then(r => r.json());
      const newTech = Array.isArray(techs) ? techs.find(t => t.email === tEmail.trim()) : null;
      if (newTech) {
        const res = await sb.fn("create-tech-account", { technician_id: newTech.id, name: tName.trim(), email: tEmail.trim(), redirectTo: window.location.origin + "/reset-password" });
        if (res.error) notify("Tech added but login failed: " + res.error, "error");
        else if (res.email_sent === false) notify(res.warning || "Tech added, but the invite email failed to send — resend it.", "error");
        else notify(`Invite sent to ${tEmail} to set their password.`);
      }
      setTName(""); setTEmail("");
      await onRefresh();
    } catch (e) { console.error(e); notify("Error adding technician", "error"); }
    setBusy(false);
  }

  async function finish() {
    setBusy(true);
    const ok = await patchOrg({ onboarding_completed: true });
    setBusy(false);
    if (!ok) { notify("Could not finish setup — check your connection and try again.", "error"); return; }
    showToast("You're all set! Welcome to iScoopCRM 🎉"); // App's toast — visible once the main CRM takes over
    await onComplete();
  }

  // Service-area + pricing steps embed full-width editors → give them room;
  // keep the business + technician steps compact.
  const cardW = (step === 2 || step === 3) ? 1180 : 720;
  const card = { background: WHITE, borderRadius: 12, padding: isMobile ? 18 : 28, boxShadow: "0 4px 24px rgba(0,0,0,.06)", width: "100%", maxWidth: cardW, boxSizing: "border-box", minWidth: 0 };
  const inp  = { width: "100%", padding: "10px 12px", border: "1px solid #dfe1e6", borderRadius: 6, fontSize: 13, fontFamily: "Inter,sans-serif", outline: "none", boxSizing: "border-box" };
  const lbl  = { display: "block", fontSize: 12, fontWeight: 500, color: "#42526e", marginBottom: 5 };
  const tzOptions = tzOptionsFor(biz.timezone);

  return (
    <div style={{ minHeight: "100vh", background: "#f5f6f8", display: "flex", flexDirection: "column", alignItems: "center", padding: isMobile ? "20px 12px 32px" : "40px 16px", boxSizing: "border-box", maxWidth: "100%", overflowX: "clip" }}>
      {/* in-wizard notice (replaces App's toast, which isn't rendered while the wizard is up) */}
      {notice && (
        <div role="status" style={isMobile
          ? { position: "fixed", top: 12, left: 12, right: 12, zIndex: 200, boxShadow: "0 8px 30px rgba(0,0,0,.18)" }
          : { width: "100%", maxWidth: cardW, marginBottom: 12 }}>
          <div className={`alert alert-${notice.type}`} style={{ marginBottom: 0 }}>{notice.type === "error" ? "❌" : "✅"} {notice.msg}</div>
        </div>
      )}
      {/* Header / progress */}
      <div style={{ width: "100%", maxWidth: cardW, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <ScoopMark size={32} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 20, color: NAVY }}>Welcome to iScoop<span style={{ color: GOLD }}>CRM</span></div>
            <div style={{ fontSize: 13, color: "#8993a4" }}>Let's set up <strong>{org?.name}</strong></div>
          </div>
        </div>
        <div style={{ display: "flex", gap: isMobile ? 6 : 8 }}>
          {STEPS.map((s, i) => (
            <div key={s} style={{ flex: 1, minWidth: 0 }}>
              <div style={{ height: 5, borderRadius: 3, background: i <= step ? GOLD : "#e3e8ef" }} />
              {/* five labels in ~65px columns wrap to 3 lines on a phone — show just the current step there */}
              {!isMobile && <div style={{ fontSize: 11, color: i === step ? NAVY : "#8993a4", fontWeight: i === step ? 700 : 500, marginTop: 5 }}>{i + 1}. {s}</div>}
            </div>
          ))}
        </div>
        {isMobile && (
          <div style={{ fontSize: 12.5, marginTop: 8, color: "#8993a4" }}>
            Step {step + 1} of {STEPS.length} · <span style={{ color: NAVY, fontWeight: 700 }}>{STEPS[step]}</span>
          </div>
        )}
      </div>

      <div style={card}>
        {/* STEP 1 — Business profile */}
        {step === 0 && (
          <div>
            <h3 style={{ margin: "0 0 4px", color: "#1a1f36" }}>Your business</h3>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#8993a4" }}>Used on invoices, receipts, and your clients' texts. You can change these later in Settings.</p>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Business name *</label>
              <input style={inp} value={biz.name} onChange={e => setBiz({ ...biz, name: e.target.value })} placeholder="e.g. Scoopers on Doody" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Your name *</label>
              <input style={inp} value={biz.contact_name} onChange={e => setBiz({ ...biz, contact_name: e.target.value })} placeholder="e.g. Luis Collazo" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Business phone</label>
              <input style={inp} value={biz.phone} onChange={e => setBiz({ ...biz, phone: formatPhoneInput(e.target.value) })} inputMode="tel" placeholder="555-555-5555" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Website <span style={{ color: "#8993a4", fontWeight: 400 }}>(optional)</span></label>
              <input style={inp} value={biz.website} onChange={e => setBiz({ ...biz, website: e.target.value })} inputMode="url" placeholder="yourbusiness.com" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Business address *</label>
              <AddressAutocomplete
                value={biz.address}
                onChange={v => setBiz(b => ({ ...b, address: v }))}
                onPick={p => setBiz(b => ({ ...b, address: p.address, city: p.city || b.city, state: p.state || b.state, zip: p.zip || b.zip }))}
                style={inp}
                placeholder="Start typing your business address…"
                confirmed={!!biz.zip}
              />
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <div style={{ flex: isMobile ? "1 1 100%" : 2, minWidth: 0 }}><label style={lbl}>City</label><input style={inp} value={biz.city} onChange={e => setBiz({ ...biz, city: e.target.value })} /></div>
              <div style={{ flex: isMobile ? "1 1 120px" : 1, minWidth: 0 }}>
                <label style={lbl}>State</label>
                <select style={{ ...inp, appearance: "auto" }} value={biz.state} onChange={e => setBiz({ ...biz, state: e.target.value })}>
                  <option value="">—</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ flex: isMobile ? "1 1 120px" : 1, minWidth: 0 }}><label style={lbl}>ZIP</label><input style={inp} value={biz.zip} onChange={e => setBiz({ ...biz, zip: onlyDigits(e.target.value, 5) })} inputMode="numeric" placeholder="12345" /></div>
            </div>
            <div style={{ marginTop: 14 }}>
              <label style={lbl}>Timezone</label>
              <select style={{ ...inp, appearance: "auto" }} value={biz.timezone} onChange={e => setBiz(b => ({ ...b, timezone: e.target.value }))}>
                {tzOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <div style={{ fontSize: 11, color: "#8993a4", marginTop: 5 }}>Used so visit days and times show correctly for your area.</div>
            </div>
            {err && <div style={{ background:"#ffebe6", border:"1px solid #ffbdad", borderRadius:6, padding:"10px 14px", fontSize:13, color:"#bf2600", marginTop:16 }}>⚠️ {err}</div>}
            <div style={{ marginTop: 16, textAlign: "right" }}>
              <button className="btn btn-gold" disabled={busy} onClick={saveBusiness}>{busy ? "Saving..." : "Continue →"}</button>
            </div>
          </div>
        )}

        {/* STEP 2 — Plan & payment (required) */}
        {step === 1 && (
          <div>
            <h3 style={{ margin: "0 0 4px", color: "#1a1f36" }}>Choose your plan</h3>
            <p style={{ margin: "0 0 18px", fontSize: 13, color: "#8993a4" }}>Pick how you'd like to pay for iScoopCRM. You can switch anytime (up to once a day) from Settings.</p>

            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 14, marginBottom: 22 }}>
              {[
                { key: "percentage", title: "Percentage", price: "2%", sub: "of what your clients pay you", blurb: "Best when you're starting out — you only pay as you earn. No monthly bill." },
                { key: "flat",       title: "Flat monthly", price: "$99", sub: "per month", blurb: "Best once you're established — predictable cost, and we take 0% of client payments." },
              ].map(opt => {
                const active = plan === opt.key;
                // Flat needs a connected Stripe account, which isn't set up until after
                // onboarding — so it's locked here. New operators start on percentage and
                // upgrade later from Settings (their card is already on file for a smooth switch).
                const locked = opt.key === "flat" && !org?.stripe_account_id;
                return (
                  <div key={opt.key} onClick={() => { if (!locked) setPlan(opt.key); }} style={{
                    flex: 1, minWidth: 0, cursor: locked ? "not-allowed" : "pointer", borderRadius: 10, padding: isMobile ? 16 : 18,
                    border: `2px solid ${active ? GOLD : "#e3e8ef"}`, background: active ? "#fffdf5" : WHITE,
                    opacity: locked ? 0.6 : 1,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontWeight: 700, color: NAVY }}>{opt.title}</span>
                      <span style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${active ? GOLD : "#c1c7d0"}`, background: active ? GOLD : "transparent" }} />
                    </div>
                    <div style={{ margin: "8px 0 2px" }}><span style={{ fontSize: 26, fontWeight: 800, color: NAVY }}>{opt.price}</span> <span style={{ fontSize: 12, color: "#8993a4" }}>{opt.sub}</span></div>
                    <div style={{ fontSize: 12, color: "#5e6c84", marginTop: 6 }}>{opt.blurb}</div>
                    {locked && <div style={{ fontSize: 11, color: "#974f0c", marginTop: 8 }}>🔒 Unlocks after you connect Stripe (in Settings). You can switch anytime later.</div>}
                  </div>
                );
              })}
            </div>

            <label style={lbl}>Card on file</label>
            <div id="operator-card-element" style={{ ...inp, padding: 12 }} />
            <div style={{ fontSize: 11, color: "#8993a4", marginTop: 6 }}>
              {plan === "flat"
                ? "Your card will be charged $99/month, starting today."
                : "No charge today — we save your card so switching plans later is instant. On this plan we keep 2% of each client payment."}
            </div>
            {cardErr && <div style={{ fontSize: 12, color: "#bf2600", marginTop: 8 }}>⚠️ {cardErr}</div>}
            {err && <div style={{ background:"#ffebe6", border:"1px solid #ffbdad", borderRadius:6, padding:"10px 14px", fontSize:13, color:"#bf2600", marginTop:12 }}>⚠️ {err}</div>}

            <div style={{ marginTop: 22, display: "flex", justifyContent: "space-between" }}>
              <button className="btn btn-ghost" onClick={() => setStep(0)}>← Back</button>
              <button className="btn btn-gold" disabled={busy} onClick={savePlan}>{busy ? "Saving..." : "Continue →"}</button>
            </div>
          </div>
        )}

        {/* STEP 3 — Service area (optional) */}
        {step === 2 && (
          <div>
            <h3 style={{ margin: "0 0 4px", color: "#1a1f36" }}>Service area <span style={{fontSize:13,fontWeight:400,color:"#8993a4"}}>(optional)</span></h3>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#8993a4" }}>Add the ZIP codes you serve and the day each is serviced — this drives scheduling. You can skip this and set it up later in Settings.</p>
            <ServiceAreaEditor serviceArea={serviceArea} onRefresh={onRefresh} showToast={notify} />
            <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between" }}>
              <button className="btn btn-ghost" onClick={() => setStep(1)}>← Back</button>
              <button className="btn btn-gold" onClick={() => setStep(3)}>{Object.keys(serviceArea || {}).length === 0 ? "Skip for now →" : "Continue →"}</button>
            </div>
          </div>
        )}

        {/* STEP 4 — Pricing */}
        {step === 3 && (
          <div>
            <h3 style={{ margin: "0 0 4px", color: "#1a1f36" }}>Pricing <span style={{fontSize:13,fontWeight:400,color:"#8993a4"}}>(optional)</span></h3>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#8993a4" }}>Set your monthly prices by frequency and number of dogs, plus deodorizing. Save when done — or skip and set it up later in Settings.</p>
            <PricingEditor showToast={notify} onRefresh={onRefresh} clients={clients} />
            <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between" }}>
              <button className="btn btn-ghost" onClick={() => setStep(2)}>← Back</button>
              <button className="btn btn-gold" onClick={() => setStep(4)}>Continue →</button>
            </div>
          </div>
        )}

        {/* STEP 5 — Technicians */}
        {step === 4 && (
          <div>
            <h3 style={{ margin: "0 0 4px", color: "#1a1f36" }}>Add a technician</h3>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#8993a4" }}>Optional — add field staff now, or skip and do it later in Settings.</p>
            {(technicians || []).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {technicians.map(t => (
                  <div key={t.id} style={{ fontSize: 13, color: "#1a1f36", padding: "4px 0" }}>✓ {t.name} <span style={{ color: "#8993a4" }}>{t.email}</span></div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <input style={{ ...inp, flex: 1, minWidth: 140 }} value={tName} onChange={e => setTName(e.target.value)} placeholder="Name" />
              <input style={{ ...inp, flex: 1, minWidth: 160 }} value={tEmail} onChange={e => setTEmail(e.target.value)} placeholder="Email" />
              <button className="btn btn-ghost" style={isMobile ? { flex: "1 1 100%", justifyContent: "center" } : undefined} disabled={busy || !tName.trim() || !tEmail.trim()} onClick={addTech}>{busy ? "…" : "+ Add"}</button>
            </div>
            <div style={{ marginTop: 28, display: "flex", justifyContent: "space-between" }}>
              <button className="btn btn-ghost" onClick={() => setStep(3)}>← Back</button>
              <button className="btn btn-gold" disabled={busy} onClick={finish}>{busy ? "Finishing..." : "Finish setup ✓"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
